import type { TrainerUIConfig } from '../../store/types';
import { DISPLAY_CONVERSION_MODAL, DISPLAY_MOTIVATIONAL_PROMPT, isFeatureEnabled } from '../../config/features';
import { IS_UI_ONLY_MODE } from '../../config/electron';
import { EXPLICIT_INTEGRATION_SOURCE } from '../../constants/appRuntime';
import { proto } from '../../proto/AsrProto';

export interface TrainerUserInterfaceConfig extends TrainerUIConfig {
    moveSaidDoSayWordsToBottom?: boolean;
    displayDoSayAlternatives?: boolean;
    explicitIntegrationSource?: number | null;
    displayCallOptOutControls?: boolean;
    displayDontSaySection?: boolean;
    displayRealTimeHints?: boolean;
}
export type TrainerTabId = 'assistant' | 'sales' | 'evaluations' | 'translator';

export const isPromptVisible = (userInterfaceConfig: TrainerUserInterfaceConfig, isCallStarted: boolean) => {
    return isFeatureEnabled(userInterfaceConfig, DISPLAY_MOTIVATIONAL_PROMPT) && !isCallStarted;
};

export const getTabsEnabled = (isSalesEnabled: boolean, isEvaluationsEnabled: boolean, isTranslatorEnabled: boolean) => {
    return isEvaluationsEnabled || isSalesEnabled || isTranslatorEnabled;
};

export const getSelectedTab = (tab: string, isTrainerEnabled: boolean, tabAssistant: string, tabSales: string) => {
    return tab || (isTrainerEnabled ? tabAssistant : tabSales);
};

export const canShowCallButton = (explicitIntegrationSource?: number | null) => {
    const manualSources: number[] = [EXPLICIT_INTEGRATION_SOURCE.I2X_MANUAL_CALL_EVENT, EXPLICIT_INTEGRATION_SOURCE.KEYPHRASE_AUDIO_EVENT];
    return manualSources.includes(explicitIntegrationSource ?? -1) && !IS_UI_ONLY_MODE;
};

export const canShowCallPopup = (isResultShown: boolean) => {
    return isResultShown;
};

export const canShowConversionModal = (
    conversionModalOpen: boolean,
    userInterfaceConfig: TrainerUserInterfaceConfig,
    callId?: string | number | null,
    userId?: string | number | null
) => {
    return conversionModalOpen && isFeatureEnabled(userInterfaceConfig, DISPLAY_CONVERSION_MODAL) && Boolean(callId) && userId != null;
};

export const canShowAudioDeviceSelectorModal = (micDeviceNotAvailable: boolean) => {
    return micDeviceNotAvailable;
};

interface CallPopupVisibilityArgs {
    isResultShown: boolean;
    isCallResultVisible: boolean;
    optInOn: boolean;
    displayCallOptOutControls?: boolean;
    wasOptInPressed?: boolean;
}

export const canShowCallPopupContent = ({
    isResultShown,
    isCallResultVisible,
    optInOn,
    displayCallOptOutControls,
    wasOptInPressed
}: CallPopupVisibilityArgs) => {
    if (!canShowCallPopup(isResultShown)) {
        return false;
    }

    let isVisible = isCallResultVisible && (optInOn || !displayCallOptOutControls);
    if (displayCallOptOutControls && wasOptInPressed === false) {
        isVisible = false;
    }

    return isVisible;
};

export const canShowOptInControls = (displayCallOptOutControls?: boolean) => {
    return Boolean(displayCallOptOutControls);
};

export const canShowDontSaySection = (displayDontSaySection?: boolean) => {
    return displayDontSaySection !== false;
};

export const isWordDetectionEnabled = ({
    isCallStarted,
    callHold,
    displayCallOptOutControls,
    optInOn
}: {
    isCallStarted: boolean;
    callHold: boolean;
    displayCallOptOutControls?: boolean;
    optInOn: boolean;
}) => {
    return isCallStarted && !callHold && (!displayCallOptOutControls || optInOn);
};

export const canShowWordDetectionCallButton = ({
    displayCallOptOutControls,
    explicitIntegrationSource
}: {
    displayCallOptOutControls?: boolean;
    explicitIntegrationSource?: number | null;
}) => {
    return Boolean(displayCallOptOutControls) || explicitIntegrationSource === proto.ExplicitIntegrationSource.I2X_MANUAL;
};

export const canShowTrainerRuntimeOverlays = (isTrainerEnabled: boolean) => {
    return isTrainerEnabled;
};

export const isTabActiveOrSinglePanelMode = (selectedTab: string, tabId: string, tabsEnabled: boolean) => {
    return selectedTab === tabId || !tabsEnabled;
};

export const canShowAssistantPanel = ({
    isTrainerEnabled,
    selectedTab,
    tabAssistant,
    tabsEnabled
}: {
    isTrainerEnabled: boolean;
    selectedTab: string;
    tabAssistant: string;
    tabsEnabled: boolean;
}) => {
    return isTrainerEnabled && isTabActiveOrSinglePanelMode(selectedTab, tabAssistant, tabsEnabled);
};

export const canShowNotification = (isNotificationShown: boolean) => {
    return isNotificationShown;
};

export const canShowCallEventNotification = (callEventNotification?: string | null) => {
    return Boolean(callEventNotification);
};

interface BuildTrainerViewStateArgs {
    tab: string;
    isTrainerEnabled: boolean;
    isSalesEnabled: boolean;
    isEvaluationsEnabled: boolean;
    isTranslatorEnabled: boolean;
    userInterfaceConfig: TrainerUserInterfaceConfig;
    tabAssistant: TrainerTabId;
    tabSales: TrainerTabId;
}

export const buildTrainerViewState = ({
    tab,
    isTrainerEnabled,
    isSalesEnabled,
    isEvaluationsEnabled,
    isTranslatorEnabled,
    userInterfaceConfig,
    tabAssistant,
    tabSales
}: BuildTrainerViewStateArgs) => {
    const tabsEnabled = getTabsEnabled(isSalesEnabled, isEvaluationsEnabled, isTranslatorEnabled);
    const selectedTab = getSelectedTab(tab, isTrainerEnabled, tabAssistant, tabSales);
    const displayRealTimeHints = userInterfaceConfig.displayRealTimeHints !== false;

    return {
        tabsEnabled,
        selectedTab,
        displayRealTimeHints
    };
};
